import React, { useContext } from 'react'
import { useIntl } from 'react-intl'
import TimeAgo, { type Formatter } from 'react-timeago'

import { cva, type VariantProps } from 'class-variance-authority'

import { AnimatedBox } from '@/components/atoms/AnimatedBox/AnimatedBox.tsx'
import { Car, type CarProps } from '@/components/atoms/Car/Car.tsx'
import { Icons } from '@/components/atoms/Icons/Icons.tsx'
import { MobileState } from '@/components/atoms/MobileState/MobileState.tsx'
import { cn } from '@/lib/utils.ts'

import { TeamsMobileStatusContext } from './TeamsStatusProvider.tsx'

const teamVariants = cva('flex cursor-pointer flex-col items-center gap-2.5 border-b py-4', {
  variants: {
    state: {
      default: 'border-b-gray-400 bg-gray-50 hover:border-b-gray-500 hover:bg-gray-200',
      active: 'border-b-orange-400 bg-orange-200',
    },
  },
  defaultVariants: {
    state: 'default',
  },
})

export interface TimeAgoFormat {
  timeAgoFormatter?: Formatter
  willExecuteInTimeAgoFormatter?: Formatter
}

export interface TeamData extends VariantProps<typeof teamVariants> {
  teamId: string
  car: Pick<CarProps, 'state' | 'carNumber' | 'carType'>
  since?: Date
  startingLocation?: string
  base?: string
  hidden?: boolean
}

export interface TeamProps extends React.HtmlHTMLAttributes<HTMLDivElement>, TeamData, TimeAgoFormat {}

export const Team = React.forwardRef<HTMLDivElement, TeamProps>(
  (
    { className, state, teamId, car, since, startingLocation, base, hidden = false, timeAgoFormatter, ...props },
    ref
  ) => {
    const teamStatus = useContext(TeamsMobileStatusContext)
    const intl = useIntl()
    return (
      <AnimatedBox className="w-full max-w-[145px]">
        {hidden ? null : (
          <div className={cn(teamVariants({ state, className }))} ref={ref} {...props}>
            <div className="flex h-[54px] w-[116px] justify-between">
              <div className="flex h-full flex-col items-center gap-3">
                <Car {...car} />
                <MobileState state={teamStatus[teamId]} />
              </div>
              <div className="inline-flex h-full w-[67px] flex-col items-start gap-1.5">
                {since && (
                  <div className="flex items-center gap-1 select-none">
                    <Icons.clockOutline className="fill-gray-500" />
                    <p className="w-[51px] truncate text-sm text-black">
                      <TimeAgo formatter={timeAgoFormatter} date={since} title={intl.formatTime(since)} />
                    </p>
                  </div>
                )}
                {startingLocation && (
                  <div className="flex items-center gap-1 select-none">
                    <Icons.phoneSquare className="fill-gray-500" />
                    <p className="w-[51px] truncate text-sm text-black">{startingLocation}</p>
                  </div>
                )}
                {base && (
                  <div className="flex items-center gap-1 select-none">
                    <Icons.base className="fill-gray-500" />
                    <p className="w-[51px] truncate text-sm text-black">{base}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </AnimatedBox>
    )
  }
)
